/**
 * Minecraft-style health hearts + hunger bar — sits above hotbar.
 */

export type StatusBars = {
  updateHealth: (current: number, max: number) => void;
  updateHunger: (current: number, max: number) => void;
  dispose: () => void;
};

const ICON_COUNT = 10; // 10 hearts = 20 HP, 10 hunger = 20 food
const ICON_SIZE = 10;
const ICON_GAP = 1;

function createHeartSVG(fill: "full" | "half" | "empty"): string {
  const colors = {
    full: "#e53e3e",
    half: "#e53e3e",
    empty: "#3a1a1a",
  };
  const bgColor = fill === "empty" ? "#3a1a1a" : "#1a0a0a";

  if (fill === "half") {
    return `<svg viewBox="0 0 9 8" width="${ICON_SIZE}" height="${ICON_SIZE}">
      <path d="M4.5,1.5 C4.5,0 2.5,0 1,1 C-0.5,2 0.5,4.5 4.5,7.5 C8.5,4.5 9.5,2 8,1 C6.5,0 4.5,0 4.5,1.5Z" fill="${bgColor}" stroke="#1a0505" stroke-width="0.5"/>
      <clipPath id="hh"><rect x="0" y="0" width="4.5" height="8"/></clipPath>
      <path d="M4.5,1.5 C4.5,0 2.5,0 1,1 C-0.5,2 0.5,4.5 4.5,7.5 C8.5,4.5 9.5,2 8,1 C6.5,0 4.5,0 4.5,1.5Z" fill="${colors.half}" clip-path="url(#hh)"/>
    </svg>`;
  }

  return `<svg viewBox="0 0 9 8" width="${ICON_SIZE}" height="${ICON_SIZE}">
    <path d="M4.5,1.5 C4.5,0 2.5,0 1,1 C-0.5,2 0.5,4.5 4.5,7.5 C8.5,4.5 9.5,2 8,1 C6.5,0 4.5,0 4.5,1.5Z" fill="${colors[fill]}" stroke="#1a0505" stroke-width="0.5"/>
  </svg>`;
}

function createHungerSVG(fill: "full" | "half" | "empty"): string {
  const colors = {
    full: "#c8903a",
    half: "#c8903a",
    empty: "#3a2a1a",
  };

  if (fill === "half") {
    return `<svg viewBox="0 0 9 9" width="${ICON_SIZE}" height="${ICON_SIZE}">
      <ellipse cx="4.5" cy="5" rx="3.5" ry="3" fill="#3a2a1a" stroke="#2a1a0a" stroke-width="0.5"/>
      <rect x="3.5" y="1" width="2" height="4" rx="0.8" fill="#3a2a1a" stroke="#2a1a0a" stroke-width="0.5"/>
      <clipPath id="hf"><rect x="0" y="0" width="4.5" height="9"/></clipPath>
      <g clip-path="url(#hf)">
        <ellipse cx="4.5" cy="5" rx="3.5" ry="3" fill="${colors.half}"/>
        <rect x="3.5" y="1" width="2" height="4" rx="0.8" fill="${colors.half}"/>
      </g>
    </svg>`;
  }

  return `<svg viewBox="0 0 9 9" width="${ICON_SIZE}" height="${ICON_SIZE}">
    <ellipse cx="4.5" cy="5" rx="3.5" ry="3" fill="${colors[fill]}" stroke="#2a1a0a" stroke-width="0.5"/>
    <rect x="3.5" y="1" width="2" height="4" rx="0.8" fill="${fill === 'empty' ? '#3a2a1a' : '#b8802a'}" stroke="#2a1a0a" stroke-width="0.5"/>
  </svg>`;
}

export function createStatusBars(): StatusBars {
  const container = document.createElement("div");
  container.id = "status-bars";
  container.style.cssText = `
    position: absolute;
    bottom: 74px;
    left: 50%;
    transform: translateX(-50%);
    width: 450px;
    display: flex;
    justify-content: space-between;
    pointer-events: none;
    padding: 0 8px;
  `;

  // Health hearts (left)
  const healthRow = document.createElement("div");
  healthRow.style.cssText = `display: flex; gap: ${ICON_GAP}px;`;

  const heartElements: HTMLSpanElement[] = [];
  for (let i = 0; i < ICON_COUNT; i++) {
    const span = document.createElement("span");
    span.innerHTML = createHeartSVG("full");
    span.style.cssText = `display: inline-flex; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.6));`;
    healthRow.appendChild(span);
    heartElements.push(span);
  }
  container.appendChild(healthRow);

  // Hunger (right)
  const hungerRow = document.createElement("div");
  hungerRow.style.cssText = `display: flex; gap: ${ICON_GAP}px;`;

  const hungerElements: HTMLSpanElement[] = [];
  for (let i = 0; i < ICON_COUNT; i++) {
    const span = document.createElement("span");
    span.innerHTML = createHungerSVG("full");
    span.style.cssText = `display: inline-flex; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.6));`;
    hungerRow.appendChild(span);
    hungerElements.push(span);
  }
  container.appendChild(hungerRow);

  const overlay = document.getElementById("ui-overlay");
  if (overlay) overlay.appendChild(container);

  // Track HP across frames so we only react to actual drops (updateHealth
  // is called every frame). -1 sentinel: the first call never shakes.
  let lastHealth = -1;

  function updateHealth(current: number, max: number) {
    const healthPerIcon = max / ICON_COUNT;
    for (let i = 0; i < ICON_COUNT; i++) {
      const iconHealth = current - i * healthPerIcon;
      if (iconHealth >= healthPerIcon) {
        heartElements[i].innerHTML = createHeartSVG("full");
      } else if (iconHealth >= healthPerIcon / 2) {
        heartElements[i].innerHTML = createHeartSVG("half");
      } else {
        heartElements[i].innerHTML = createHeartSVG("empty");
      }
    }

    // Took damage → shake the hearts so the loss is impossible to miss.
    if (current < lastHealth - 0.01) {
      healthRow.animate(
        [
          { transform: "translateX(0) scale(1)" },
          { transform: "translateX(-4px) scale(1.15)" },
          { transform: "translateX(4px) scale(1.15)" },
          { transform: "translateX(-3px) scale(1.08)" },
          { transform: "translateX(2px) scale(1.04)" },
          { transform: "translateX(0) scale(1)" },
        ],
        { duration: 320, easing: "ease-out" },
      );
    }
    lastHealth = current;
  }

  function updateHunger(current: number, max: number) {
    const hungerPerIcon = max / ICON_COUNT;
    for (let i = 0; i < ICON_COUNT; i++) {
      const iconHunger = current - i * hungerPerIcon;
      if (iconHunger >= hungerPerIcon) {
        hungerElements[i].innerHTML = createHungerSVG("full");
      } else if (iconHunger >= hungerPerIcon / 2) {
        hungerElements[i].innerHTML = createHungerSVG("half");
      } else {
        hungerElements[i].innerHTML = createHungerSVG("empty");
      }
    }
  }

  function dispose() {
    container.remove();
  }

  return { updateHealth, updateHunger, dispose };
}
